import { Link, useLocation } from 'react-router-dom';
import { therapistProfilePath } from './workspace/clientCarePaths';
import { formatSpecialization, formatTherapistLanguages } from '@lighthouse/shared';
import { Portrait } from './Avatar';
import { TherapistSpecializationPills } from './TherapistSpecializationPills';

export interface TherapistCardData {
  id: string;
  displayName: string;
  bio: string;
  hourlyRateNgn: number;
  ratingAvg: number;
  ratingCount: number;
  specializations: string[];
  languages?: string[];
  gender?: string;
  faithAffiliation?: string | null;
  profilePhotoUrl?: string | null;
}

interface TherapistCardProps {
  therapist: TherapistCardData;
  footer?: React.ReactNode;
}

function renderStars(avg: number) {
  return Array.from({ length: 5 }).map((_, i) => (
    <span key={i} className={i < Math.round(avg) ? 'text-amber-400' : 'text-gray-200'}>
      ★
    </span>
  ));
}

export function TherapistCard({ therapist, footer }: TherapistCardProps) {
  const { pathname } = useLocation();
  const [focus] = therapist.specializations;
  return (
    <Link to={therapistProfilePath(pathname, therapist.id)} className="therapist-card group">
      <Portrait name={therapist.displayName} src={therapist.profilePhotoUrl} />

      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-serif text-lg font-medium leading-tight text-stone-900">
          {therapist.displayName}
        </h3>
        <p className="mt-1 text-sm text-stone-500">
          {focus ? formatSpecialization(focus) : 'Licensed clinician'}
        </p>

        <div className="mt-2 flex items-center gap-0.5 text-xs">
          {renderStars(therapist.ratingAvg)}
          {therapist.ratingCount > 0 ? (
            <span className="ml-1 font-medium text-stone-500">
              {therapist.ratingAvg.toFixed(1)} ({therapist.ratingCount})
            </span>
          ) : (
            <span className="ml-1 text-stone-400">New</span>
          )}
        </div>

        <div className="mt-3">
          <TherapistSpecializationPills
            specializations={therapist.specializations}
            limit={2}
            label="Specializes in"
          />
        </div>

        <p className="mt-3 line-clamp-2 flex-1 text-sm leading-relaxed text-stone-600">
          {therapist.bio}
        </p>

        {therapist.languages && therapist.languages.length > 0 && (
          <p className="mt-2 text-xs text-stone-500">
            {formatTherapistLanguages(therapist.languages)}
          </p>
        )}

        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-stone-900">
            ₦{therapist.hourlyRateNgn.toLocaleString()}
          </p>
          {footer ?? <span className="text-primary-700 text-sm font-semibold">View profile</span>}
        </div>
      </div>
    </Link>
  );
}
